/**
 * Shared auth-related constants used across server actions, client forms,
 * and the auth callback route.
 */
import {
  SIGNUP_PRESET_PARAM,
  isPlausibleSignupPresetCode,
} from "@brev-io/common/src/types/org-signup-preset-code";
import { RETURN_TO_PARAM, sanitizeReturnTo } from "./auth/return-to";

export const PUBLIC_EMAIL_ERROR =
  "Please use your work email to continue. Personal email addresses are not supported.";

export const DISPOSABLE_EMAIL_ERROR =
  "This email address isn't supported. Please use a permanent work email to continue.";

/**
 * Copy surfaced to users when WorkOS Radar blocks an attempt. Intentionally
 * vague about which signal fired — attackers should not learn whether they hit
 * an IP blocklist, bot detection, or impossible-travel. Diverges by login vs
 * signup so legitimate users get a self-service path (login → retry/support,
 * signup → try a different method).
 *
 * These live here (not in the "use server" auth-custom.ts) so both the server
 * actions and the client-side analytics classifier (auth-errors.ts) can
 * reference the exact same strings without either re-typing the copy or a
 * client module importing a server-only file. See BRE-4751.
 */
export const RADAR_BLOCK_LOGIN_ERROR =
  "We couldn't sign you in right now. If you believe this is a mistake, contact support using the button below.";
export const RADAR_BLOCK_SIGNUP_ERROR =
  "We couldn't create this account right now. Try a different sign-up method, or contact support using the button below.";

/**
 * WorkOS's own `sso_required` refusal and Brev's "Require SSO to sign in"
 * (BRE-8955) show this same copy; the login card matches on "SSO" to reset to
 * the SSO button.
 */
export const SSO_REQUIRED_ERROR = "Your organization requires SSO. Please use the SSO option above to sign in.";

/**
 * Extract the lowercase domain from an email address.
 * Returns null if the email is malformed (no @ or no domain part).
 */
export function extractEmailDomain(email: string): string | null {
  if (!email || typeof email !== "string") return null;
  const parts = email.toLowerCase().trim().split("@");
  if (parts.length !== 2 || !parts[1]) return null;
  return parts[1];
}

/**
 * Build an auth-page href that carries forward everything the current page was
 * opened with — the invitation token, the `/signup?code=` preset code, and the
 * post-sign-in `returnTo` (BRE-7698).
 *
 * BRE-6058: the preset code is normally parked in an HttpOnly cookie by the
 * middleware, but the `/signup` ↔ `/login` cross-links previously dropped it
 * from the URL, leaving the cookie as the single point of failure for a link
 * that decides how the customer's org is provisioned. Keeping it in the href
 * gives the middleware a second chance to plant the cookie on the page the
 * user lands on. Only shape-valid codes are reflected back into a link.
 */
export function buildAuthHref(
  path: "/login" | "/signup",
  opts: { invitationToken?: string; signupPresetCode?: string; returnTo?: string },
): string {
  const params = new URLSearchParams();
  if (opts.invitationToken) {
    params.set("invitation_token", opts.invitationToken);
  }
  if (isPlausibleSignupPresetCode(opts.signupPresetCode)) {
    params.set(SIGNUP_PRESET_PARAM, opts.signupPresetCode);
  }
  const returnTo = sanitizeReturnTo(opts.returnTo);
  if (returnTo) {
    params.set(RETURN_TO_PARAM, returnTo);
  }
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}
